import React, { useState, useEffect } from 'react';
import { 
  Modal, 
  Form, 
  Button, 
  Space, 
  Typography,
  Input,
  Switch,
  Checkbox,
  Row,
  Col,
  Spin,
  Card
} from 'antd';
import { 
  SaveOutlined,
  LoadingOutlined,
  UserOutlined,
  MedicineBoxOutlined,
  BankOutlined,
  CalendarOutlined,
  TeamOutlined,
  SettingOutlined,
  SafetyOutlined
} from '@ant-design/icons';
import { useTranslation } from '../../hooks/useTranslation';
import { AdminRole, AdminPermission, PERMISSION_METADATA } from './types';
import { useQuery } from '@apollo/client';
import { GET_AVAILABLE_ADMIN_PERMISSIONS } from '../../graphql/mutations';

const { Text } = Typography;

interface RoleModalProps {
  visible: boolean;
  role?: AdminRole | null;
  isSubmitting: boolean;
  form: any;
  onCancel: () => void;
  onSubmit: (values: any) => void;
}

const RoleModal: React.FC<RoleModalProps> = ({ 
  visible, 
  role, 
  isSubmitting, 
  form, 
  onCancel, 
  onSubmit
}) => {
  const { t } = useTranslation();
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  
  // Fetch available permissions from API
  const { data: permissionsData, loading: loadingPermissions } = useQuery(GET_AVAILABLE_ADMIN_PERMISSIONS);
  
  // Transform the permission data to include metadata
  const availablePermissions = (permissionsData?.availableAdminPermissions || [])
    .map((permission: string) => {
      const metadata = PERMISSION_METADATA[permission];
      return {
        id: permission,
        permission: permission,
        name: metadata?.name || permission,
        description: metadata?.description || permission,
        category: metadata?.category || 'other',
        is_active: true
      };
    });

  const isEdit = !!role?.id;

  // Helper function to get category colors and icons
  const getCategoryConfig = (category: string) => {
    const configs: Record<string, { color: string; icon: React.ReactNode; bgColor: string }> = {
      'userManagement': { 
        color: '#1890ff', 
        icon: <UserOutlined />, 
        bgColor: '#f0f8ff' 
      },
      'doctorManagement': { 
        color: '#52c41a', 
        icon: <MedicineBoxOutlined />, 
        bgColor: '#f6ffed' 
      },
      'clinicManagement': { 
        color: '#722ed1', 
        icon: <BankOutlined />, 
        bgColor: '#f9f0ff' 
      },
      'appointmentManagement': { 
        color: '#fa8c16', 
        icon: <CalendarOutlined />, 
        bgColor: '#fff7e6' 
      },
      'providerManagement': { 
        color: '#13c2c2', 
        icon: <TeamOutlined />, 
        bgColor: '#e6fffb' 
      },
      'patientManagement': { 
        color: '#fa541c', 
        icon: <UserOutlined />, 
        bgColor: '#fff2e8' 
      },
      'systemManagement': { 
        color: '#000000', 
        icon: <SettingOutlined />, 
        bgColor: '#f5f5f5' 
      }
    };
    return configs[category] || { color: '#1890ff', icon: <SafetyOutlined />, bgColor: '#f0f8ff' };
  };

  // Ensure form is properly populated when modal opens
  useEffect(() => {
    if (visible) {
      if (role && isEdit) {
        // Editing existing role
        const currentPermissions = role.permissions?.map((p: any) => p.permission) || [];
        setSelectedPermissions(currentPermissions);
        form.setFieldsValue({
          name: role.name,
          description: role.description,
          is_active: role.is_active !== false,
          permission_ids: currentPermissions
        });
      } else {
        // Creating new role
        setSelectedPermissions([]);
        form.setFieldsValue({
          is_active: true,
          permission_ids: []
        });
      }
    }
  }, [visible, role, isEdit, form]);

  const handlePermissionChange = (checkedValues: string[]) => {
    setSelectedPermissions(checkedValues);
    form.setFieldsValue({ permission_ids: checkedValues });
  };

  const handleCategoryToggle = (category: string, permissions: AdminPermission[], selectAll: boolean) => {
    const categoryPermissions = permissions.map(p => p.permission);
    let newSelectedPermissions: string[];
    
    if (selectAll) {
      // Add all category permissions
      const combined = [...selectedPermissions, ...categoryPermissions];
      newSelectedPermissions = Array.from(new Set(combined));
    } else {
      // Remove all category permissions
      newSelectedPermissions = selectedPermissions.filter(p => !categoryPermissions.includes(p));
    }
    
    setSelectedPermissions(newSelectedPermissions);
    form.setFieldsValue({ permission_ids: newSelectedPermissions });
  };

  const groupedPermissions = availablePermissions.reduce((acc: Record<string, AdminPermission[]>, permission: AdminPermission) => {
    if (!acc[permission.category]) {
      acc[permission.category] = [];
    }
    acc[permission.category].push(permission);
    return acc;
  }, {} as Record<string, AdminPermission[]>);

  return (
    <Modal
      title={isEdit ? t('adminUsers.editRole') : t('adminUsers.createRole')}
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={800}
    >
      {loadingPermissions ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
          <div style={{ marginTop: '16px' }}>
            <Text type="secondary">{t('adminUsers.loadingPermissions')}</Text>
          </div>
        </div>
      ) : (
        <Form
          form={form}
          layout="vertical"
          onFinish={onSubmit}
        >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={t('adminUsers.roleName')}
              name="name"
              rules={[{ required: true, message: t('adminUsers.pleaseInputRoleName') }]}
            >
              <Input placeholder={t('adminUsers.enterRoleName')} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={t('common.status')}
              name="is_active"
              valuePropName="checked"
            >
              <Switch checkedChildren={t('adminUsers.statuses.active')} unCheckedChildren={t('adminUsers.statuses.inactive')} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label={t('adminUsers.description')}
          name="description"
          rules={[{ required: true, message: t('adminUsers.pleaseInputDescription') }]}
        >
          <Input.TextArea 
            placeholder={t('adminUsers.enterDescription')} 
            rows={3}
          />
        </Form.Item>

        <Form.Item
          label={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>{t('adminUsers.permissionsLabel')}</span>
              <div style={{ 
                fontSize: '12px', 
                color: '#8c8c8c',
                backgroundColor: '#f5f5f5',
                padding: '2px 8px',
                borderRadius: '12px'
              }}>
                {selectedPermissions.length} of {availablePermissions.length} selected
              </div>
            </div>
          }
          name="permission_ids"
          initialValue={selectedPermissions}
        >
          <Checkbox.Group 
            style={{ width: '100%' }}
            value={selectedPermissions}
            onChange={handlePermissionChange}
            key={`permissions-${role?.id || 'new'}`}
          >
            <div style={{ 
              marginBottom: '12px', 
              padding: '8px 12px', 
              backgroundColor: '#fafafa', 
              borderRadius: '6px',
              border: '1px solid #d9d9d9'
            }}>
              <Space>
                <Button
                  size="small"
                  type="primary"
                  ghost
                  onClick={() => {
                    const allPermissions = availablePermissions.map((p: AdminPermission) => p.permission);
                    setSelectedPermissions(allPermissions);
                    form.setFieldsValue({ permission_ids: allPermissions });
                  }}
                >
                  Select All Permissions
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    setSelectedPermissions([]);
                    form.setFieldsValue({ permission_ids: [] });
                  }}
                >
                  Clear All
                </Button>
              </Space>
            </div>
            <div style={{ maxHeight: '400px', overflowY: 'auto', padding: '8px' }}>
              {Object.entries(groupedPermissions).map(([category, permissions]) => {
                const config = getCategoryConfig(category);
                return (
                  <Card
                    key={category}
                    size="small"
                    style={{ 
                      marginBottom: '16px',
                      border: `1px solid ${config.color}20`,
                      backgroundColor: config.bgColor
                    }}
                    bodyStyle={{ padding: '12px' }}
                  >
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      marginBottom: '12px',
                      paddingBottom: '8px',
                      borderBottom: `1px solid ${config.color}30`
                    }}>
                      <div style={{ 
                        color: config.color, 
                        fontSize: '16px', 
                        marginRight: '8px',
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        {config.icon}
                      </div>
                      <Typography.Text strong style={{ 
                        color: config.color, 
                        fontSize: '14px'
                      }}>
                        {t(`adminUsers.permissionCategories.${category}`)}
                      </Typography.Text>
                      <div style={{ 
                        marginLeft: 'auto', 
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <div style={{ 
                          fontSize: '12px', 
                          color: '#8c8c8c',
                          backgroundColor: '#fff',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          border: `1px solid ${config.color}30`
                        }}>
                          {(permissions as AdminPermission[]).length} permissions
                        </div>
                        <Space size="small">
                          <Button
                            size="small"
                            type="text"
                            style={{ 
                              fontSize: '11px', 
                              color: config.color,
                              padding: '2px 6px',
                              height: 'auto'
                            }}
                            onClick={() => handleCategoryToggle(category, permissions as AdminPermission[], true)}
                          >
                            Select All
                          </Button>
                          <Button
                            size="small"
                            type="text"
                            style={{ 
                              fontSize: '11px', 
                              color: '#8c8c8c',
                              padding: '2px 6px',
                              height: 'auto'
                            }}
                            onClick={() => handleCategoryToggle(category, permissions as AdminPermission[], false)}
                          >
                            Clear
                          </Button>
                        </Space>
                      </div>
                    </div>
                    <Row gutter={[8, 8]}>
                      {(permissions as AdminPermission[]).map((permission: AdminPermission) => (
                        <Col span={12} key={permission.id}>
                          <Checkbox 
                            value={permission.permission}
                            style={{ 
                              width: '100%',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              backgroundColor: selectedPermissions.includes(permission.permission) 
                                ? `${config.color}10` 
                                : 'transparent',
                              border: selectedPermissions.includes(permission.permission) 
                                ? `1px solid ${config.color}30` 
                                : '1px solid transparent',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <span style={{ fontSize: '12px' }}>
                              {t(`adminUsers.permissions.${permission.name}`)}
                            </span>
                          </Checkbox>
                        </Col>
                      ))}
                    </Row>
                  </Card>
                );
              })}
            </div>
          </Checkbox.Group>
        </Form.Item>

        <Form.Item style={{ marginTop: 24, textAlign: 'right' }}>
          <Space>
            <Button onClick={onCancel}>
              {t('common.cancel')}
            </Button>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={isSubmitting}
              icon={isSubmitting ? <LoadingOutlined /> : <SaveOutlined />}
            >
              {isSubmitting ? t('adminUsers.saving') : (isEdit ? t('adminUsers.update') : t('adminUsers.create'))}
            </Button>
          </Space>
        </Form.Item>
        </Form>
      )}
    </Modal>
  );
};

export default RoleModal;
